/**
 * `client-account-number-mark` (crossmarc/steward-app#2279, "Information requests 1") — the
 * firm's own record that, for ONE account on a client's snapshot, the full account number is
 * already on file somewhere the firm keeps it. Who said so, and when. One row per
 * (client, account), in the firm's own workspace.
 *
 * ## It records a fact about the firm's filing. It is not built to hold a number.
 *
 * The row names the account (`accountKey`, `accountLabel`) and the person who marked it. The
 * strict schema below refuses any attribute it does not name. What each free-ish field can carry:
 *
 *   - `accountKey`: {@link AccountKeySchema} admits only `row:` or `fp:`, then 16 letters `a` to
 *     `p`, then optionally `#` and at most three digits. The façade never stores free text or an
 *     account number there. What a caller can still do is choose the 16 letters themselves; that
 *     matches no account and shows only as an orphan under its label. Pinned, including the
 *     refusals, in `clientAccountNumberMark.test.ts`.
 *   - `accountLabel` IS free text: the display name the SPA builds from what the firm already sees
 *     (institution, account name, the bank's own displayed mask of at most 8 characters). The
 *     façade never refuses a label. If {@link looksLikeAccountNumber} sees a run of nine or more
 *     digits in it, {@link redactNumberLikeLabel} stores it with every digit replaced by `#`. That
 *     is acceptable because the stored label is shown only for an ORPHAN mark (one whose key
 *     matches no current account); every other row is labelled from the live snapshot. **What
 *     still passes, plainly:** a complete number of 8 digits or fewer, a number with letters mixed
 *     in, and digits broken up by any separator other than whitespace, hyphens, `.`, `/` and `_`.
 *     The façade cannot check a label against the real account. This is a net for an accidental
 *     paste, not a control.
 *   - `markedByName` is the caller's profile name, cleaned by `safeDisplayText` before it is stored
 *     (a test fails if that call is dropped).
 *
 * ## Placement and shape: cloned from `onboarding-mark`
 *
 * Same pack (`STEWARD_ADVISOR_FIRM_TYPE_DEFS`), same write shape (the façade creates it under
 * the firm caller's own bearer token, never a background role), same `accessControl`
 * (`STEWARD_WRITE_CAPABILITY`), same `strict` audit level and flat-string fields (#1859, see
 * `onboardingMark.ts`). No new capability, role, actor rule or appcore change. Unlike
 * `onboarding-mark` there are MANY rows per client, one per marked account, so the façade finds
 * them by listing the firm's rows and matching the `clientId` attribute
 * (`handlers/clientRequests.ts`).
 *
 * ## Who may read and write it
 *
 * All three routes sit behind the three gates `handlers/firmClientDocuments.ts` documents: the
 * caller's firm from `/me`, the client in that firm (404 otherwise), and `requireTeamAccess`
 * (403 unless the caller is the client's own advisor or a firm admin). The reading is gated as
 * tightly as the writing on purpose: the table tells you which accounts are missing a number.
 *
 * **Residual, as the repo's code and config show it (not a statement about any deployed
 * instance).** The team rule exists only in the façade. This type declares
 * `accessControl.writeCapability` as `STEWARD_WRITE_CAPABILITY` (`steward:write`) and NO
 * `readCapability`, so appcore itself does not narrow a firm member to a client's team: a caller
 * who could reach appcore's generic entity routes directly would not be held to the team rule. It
 * is the same residual `firmClientDocuments.ts` states for the documents type (its "Residual gap"
 * note, which gives its reasons for why a browser session is not known to reach those routes; those
 * reasons cite appcore instance profiles that are not in this repo and are not re-checked here).
 * This repo's own guard is that no route in `handlers/clientRequests.ts` forwards a
 * caller-chosen type code.
 *
 * ## An account key is a claim about the client's own snapshot, and nothing more
 *
 * See {@link accountKey}. A mark whose key matches no current account is NOT dropped and NOT
 * re-pointed at another account: the Requests tab lists it under its stored `accountLabel`.
 *
 * ## What this type is NOT
 *
 *   - **Not copied into a Steward workspace** and **not readable by the family**, for the same
 *     reasons `onboarding-mark` documents.
 *   - **Not authority over anything the family answered.** It says what the firm filed, nothing
 *     about whether the number is correct.
 *   - **Purged with the client.** Registered in `CLIENT_PURGE_MANIFEST`
 *     (`clientPurgeManifest.test.ts`) and deleted by `purgeClientAccountNumberMarkRows`.
 */
import { z } from 'zod';

export const CLIENT_ACCOUNT_NUMBER_MARK_TYPE_CODE = 'client-account-number-mark';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

/** Longest `accountKey` the schema admits: `row:` + 16 + `#999` is 24. */
export const ACCOUNT_KEY_MAX = 24;
/** Longest `accountLabel` the schema admits. */
export const ACCOUNT_LABEL_MAX = 160;

/** The zero-width characters U+200B to U+200D, U+2060 and U+FEFF. The façade also strips them
 *  from the stored label (`handlers/clientRequests.ts`). The digit-run test goes further, see
 *  {@link INVISIBLE_CHARS}. */
export const ZERO_WIDTH_CHARS = /[\u200B-\u200D\u2060\uFEFF]/g;

/** Everything that takes no (or no separate) space on screen and so could split a run of digits
 *  without showing it: format characters (`\p{Cf}`, which include the zero-width ones),
 *  combining marks (`\p{Mn}`, `\p{Me}`, which include U+034F and the variation selectors) and the
 *  Hangul fillers U+115F, U+1160, U+3164 and U+FFA0. */
export const INVISIBLE_CHARS = /[\p{Cf}\p{Mn}\p{Me}\u115F\u1160\u3164\uFFA0]/gu;

/** NFKC, then the invisible characters out. The shared first step of the guard and the redactor. */
function normalizedForDigitTest(value: string): string {
  return value.normalize('NFKC').replace(INVISIBLE_CHARS, '');
}

/**
 * True when `value` contains a run of nine or more digits once the characters people put between
 * the groups of a number are removed: whitespace, {@link INVISIBLE_CHARS}, hyphens and Unicode
 * dashes (U+2010 to U+2015, U+2212), `.`, `/` and `_`. Digits of any script count, and NFKC maps
 * look-alikes such as circled digits to plain ones first (a test pins that it matters).
 *
 * This is a net for an accidental paste, NOT a control. It does not catch: a complete number of
 * eight digits or fewer, a number with letters mixed in, or digits split by any other character
 * (a comma, a slash-like symbol, an emoji). A deliberate caller gets past it. The bank's displayed
 * mask is at most 8 characters (`firmAccountMask`, `intakeFirmView.ts`), so the accounts the SPA
 * labels itself never reach nine; a realistic label with a date, a rate or a phone number can,
 * which is why the façade redacts such a label instead of refusing it ({@link redactNumberLikeLabel}).
 */
export function looksLikeAccountNumber(value: string): boolean {
  return /\p{Nd}{9,}/u.test(normalizedForDigitTest(value).replace(/[\s\-./_\u2010-\u2015\u2212]/g, ''));
}

/**
 * `label` unchanged unless {@link looksLikeAccountNumber} trips, in which case it is returned
 * after the same normalisation with EVERY digit (any script) replaced by `#`, cut to `max`
 * UTF-16 code units without splitting a surrogate pair ({@link ACCOUNT_LABEL_MAX} unless the
 * caller names a longer field) and trimmed after the cut. Never refuses. (Free text uses
 * {@link redactNumberRuns} instead, which leaves the rest of the sentence alone.) Redaction is acceptable because the stored
 * label is shown only for an orphan mark; a live account is labelled from the snapshot.
 */
export function redactNumberLikeLabel(label: string, max = ACCOUNT_LABEL_MAX): string {
  if (!looksLikeAccountNumber(label)) return label;
  // Cut by UTF-16 units (what a schema `.max()` counts) without splitting a pair, then trim
  // again: the cut can land just after a space, and the result must be a fixed point.
  return clipUtf16(normalizedForDigitTest(label).replace(/\p{Nd}/gu, '#'), max).trim();
}

/**
 * `value` cut to at most `max` UTF-16 code units (the unit a Zod `.max()` counts) without ever
 * splitting a surrogate pair. Not trimmed: callers that need a fixed point trim after.
 */
export function clipUtf16(value: string, max: number): string {
  let out = '';
  for (const ch of value) {
    if (out.length + ch.length > max) break;
    out += ch;
  }
  return out;
}

const NUMBER_SEPARATOR = /^[\s\-./_\u2010-\u2015\u2212]$/u;
const WHOLE_DIGITS = /^\p{Nd}+$/u;

/**
 * For FREE text (an information-request prompt, an account's institution): like
 * {@link redactNumberLikeLabel} but it replaces only the digits of the run that tripped the guard,
 * so "Form 1099 for 2023" and "ending 4821" are untouched and "call 555 123 4567 about the 2023
 * return" keeps the rest of the sentence. A run is the same thing {@link looksLikeAccountNumber}
 * sees: digits of any script (after NFKC, so a circled digit counts) joined only by the
 * characters it ignores, nine or more of them.
 *
 * Redaction is done IN PLACE, one character at a time: every character outside the matched run
 * (including combining marks such as Thai and Devanagari vowel signs) is returned exactly as it
 * was, and inside a run only the digits change (each becomes `#`; a character that normalises to
 * two digits becomes two). The per-character reading can disagree with the detector's whole-string
 * NFKC in exotic cases; when the result would still trip the guard, this falls back to the
 * detector's own normalised form (every digit `#`, combining marks dropped from the whole text),
 * which cannot. That fallback is the only case where text outside a run changes, and it keeps the
 * result a fixed point. Never refuses and never clips.
 */
export function redactNumberRuns(value: string): string {
  if (!looksLikeAccountNumber(value)) return value;
  type Tok = { text: string; kind: 'digit' | 'skip' | 'other'; digits: number };
  const toks: Tok[] = Array.from(value, (text): Tok => {
    const n = text.normalize('NFKC');
    if (n.replace(INVISIBLE_CHARS, '') === '' || NUMBER_SEPARATOR.test(n)) return { text, kind: 'skip', digits: 0 };
    if (WHOLE_DIGITS.test(n)) return { text, kind: 'digit', digits: Array.from(n).length };
    return { text, kind: 'other', digits: 0 };
  });
  let out = '';
  for (let i = 0; i < toks.length;) {
    if (toks[i]!.kind !== 'digit') { out += toks[i]!.text; i++; continue; }
    let j = i; let last = i; let count = 0;
    while (j < toks.length && toks[j]!.kind !== 'other') {
      if (toks[j]!.kind === 'digit') { last = j; count += toks[j]!.digits; }
      j++;
    }
    for (let k = i; k <= last; k++) {
      const t = toks[k]!;
      out += count >= 9 && t.kind === 'digit' ? '#'.repeat(t.digits) : t.text;
    }
    i = last + 1;
  }
  return looksLikeAccountNumber(out) ? normalizedForDigitTest(value).replace(/\p{Nd}/gu, '#') : out;
}

/**
 * True when `value` holds what {@link redactNumberRuns} leaves behind: nine or more `#` joined
 * only by the separators a number run allows. A heuristic for telling the user "a long number
 * was hidden": someone who typed that many hash marks is told too, which does no harm.
 */
export function containsRedactedNumber(value: string): boolean {
  return /#(?:[\s\-./_\u2010-\u2015\u2212\p{Cf}\p{Mn}\p{Me}]*#){8,}/u.test(value);
}

/** The label a mark stores. Non-blank, bounded, and not number-shaped (a label that is is
 *  redacted by the façade before it gets here, so the refusal is the net under that step). */
export const AccountLabelSchema = z.string().min(1).max(ACCOUNT_LABEL_MAX)
  .refine((value) => !looksLikeAccountNumber(value), 'accountLabel must not contain an account number');

/** What the browser mints for a hand-typed row (`newRowId` in `src/intake/draft.ts`): 32 lowercase
 *  hex characters. `INTAKE_ROW_ID_PATTERN` admits more (16 to 32 letters and digits); only the
 *  narrower thing that is actually minted takes the `row:` path in {@link accountKey}. */
export const MINTED_ROW_ID_PATTERN = /^[0-9a-f]{32}$/;

/**
 * The exact shape of an `accountKey`, and the whole of what it can carry:
 *
 *   - `row:` or `fp:`, then 16 letters `a` to `p` (a {@link fingerprintDigest}: 64 bits, no
 *     text and no digits);
 *   - then optionally `#` and one to three digits (the twin ordinal, {@link accountKey}).
 *
 * So the only digits a key can hold are the at most three of the ordinal; the schema has no
 * place for words, punctuation or an account number. The row id itself is never stored: a
 * `row:` key is the digest of the minted id, so a raw 32-hex id (which could hold 32 typed
 * decimal digits) is refused, and so is a 15- or 17-letter digest. What the shape does NOT stop:
 * a caller can still choose their own 16 letters `a` to `p`. That matches no account, and shows
 * only as an orphan mark under its label.
 */
export const AccountKeySchema = z.string().max(ACCOUNT_KEY_MAX)
  .regex(/^(row|fp):[a-p]{16}(#\d{1,3})?$/, 'accountKey must be a fingerprint digest');

/**
 * The STORED row. Every field is a flat string (#1859). `strictObject` is load-bearing: it is
 * what makes "there is no field that could hold a number" true of what the façade will write, and
 * `clientAccountNumberMark.test.ts` pins both the key set and the refusal of an extra key.
 */
export const ClientAccountNumberMarkAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this mark belongs to (that type's own appcore `instanceId`). */
  clientId: id,
  /** See {@link accountKey}. */
  accountKey: AccountKeySchema,
  /** What the account was called on the snapshot when it was marked. */
  accountLabel: AccountLabelSchema,
  markedByUserId: id,
  markedByName: z.string().min(1).max(200),
  markedAt: instant,
});

export type ClientAccountNumberMarkAttributes = z.infer<typeof ClientAccountNumberMarkAttributesSchema>;

/** The attribute names a stored mark may carry. Exported so a test can pin the set. */
export const CLIENT_ACCOUNT_NUMBER_MARK_KEYS = Object.keys(ClientAccountNumberMarkAttributesSchema.shape);

/** Reads a stored row's attributes. `unreadable` covers both "does not parse" and "is missing". */
export function readClientAccountNumberMark(
  value: unknown,
): { kind: 'mark'; mark: ClientAccountNumberMarkAttributes } | { kind: 'unreadable' } {
  const parsed = ClientAccountNumberMarkAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'mark', mark: parsed.data } : { kind: 'unreadable' };
}

// ── Account keys ───────────────────────────────────────────────────────────────────────────

/** Longest piece of text a fingerprint takes from any one field. */
const KEY_PART_MAX = 80;

/** The sections a Requests row can sit under. `other` is an account the snapshot cannot place. */
export type RequestAccountSection = 'cash' | 'investments' | 'other';

/** What {@link accountKey} reads from one account. Plain data, no I/O. */
export interface AccountKeyInput {
  section: RequestAccountSection;
  /** The id a hand-typed row carries (`intakeRowId.ts`). A connected account has none. */
  rowId?: string | undefined;
  institution?: string | undefined;
  /** The account's own name (connected) or its kind (typed): "Total Checking", "Roth IRA". Also
   *  what the label shows after the institution. */
  name?: string | undefined;
  /** The bank's displayed mask, when there is one. */
  mask?: string | undefined;
}

function part(value: string | undefined): string {
  return (value ?? '').normalize('NFKC').toLowerCase().replace(/[|#]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, KEY_PART_MAX);
}

const FNV64_OFFSET = '0xcbf29ce484222325';
const FNV64_PRIME = '0x100000001b3';

/**
 * A 64-bit FNV-1a digest of `text` (UTF-8 bytes), written as 16 letters `a` to `p` (four bits
 * apiece, no digits). Deterministic and synchronous, so the SPA and the façade compute the same
 * value without a shared service. NOT a security primitive: everything hashed is already shown
 * to the reader of the table, and the point of hashing is only that the stored key then holds no
 * text at all.
 *
 * Collision bound, stated as arithmetic and assuming the digest is close to uniform (FNV-1a is
 * not proven to be, so this is an estimate and not a guarantee): with n accounts under one
 * client the chance that two different fingerprints share a digest is about n(n-1)/2 / 2^64.
 * For n = 200 (more accounts than any snapshot this app builds lists, and the per-client mark
 * cap) that is 19,900 / 1.8447e19, about 1.1e-15. A collision only ever matters inside one
 * client's own list, where `listRequestAccounts` treats the two as twins (the second gets `#2`),
 * with the twin caveats in {@link accountKey}.
 *
 * The two kinds of key cannot collide with each other by construction: the `row:` and `fp:`
 * prefixes differ, and the hashed inputs are domain-separated as well (`row|<id>` against
 * `fp|<section>|...`), so the same digest from one kind never names the other's account.
 */
export function fingerprintDigest(text: string): string {
  const mask = (BigInt(1) << BigInt(64)) - BigInt(1);
  const prime = BigInt(FNV64_PRIME);
  let hash = BigInt(FNV64_OFFSET);
  for (const byte of new TextEncoder().encode(text)) {
    hash = ((hash ^ BigInt(byte)) * prime) & mask;
  }
  let out = '';
  for (let i = 15; i >= 0; i--) out += String.fromCharCode(97 + Number((hash >> BigInt(4 * i)) & BigInt(15)));
  return out;
}

/**
 * The stable name of one account for the purpose of a mark. Its shape is {@link AccountKeySchema}.
 *
 *   - A typed row whose `rowId` is a minted one ({@link MINTED_ROW_ID_PATTERN}) is
 *     `row:` plus the digest of `row|<rowId>`. The id is minted when the row is added and
 *     survives removing or reordering its neighbours, so this is the strong case. A row whose id
 *     is not of that shape (the data schema admits 16 to 32 letters and digits, so a legacy,
 *     uppercase or shorter id) is keyed by fingerprint instead: two such rows with the same
 *     section, institution, name and mask are order-dependent twins (see the limits below),
 *     although their statements still tie by row id.
 *   - Anything else is `fp:` plus {@link fingerprintDigest} of section, institution, name and
 *     mask, each lower-cased and whitespace-collapsed. Nothing positional goes into it: the
 *     index of the account in the snapshot is NOT part of the key, which is what lets other
 *     accounts be reordered, added or removed without moving a mark.
 *   - `ordinal` is appended (`#2`, `#3`) only for the second and later accounts that share a
 *     key, so a twin the firm cannot tell apart still gets its own key. The first is bare, so
 *     adding a twin later never changes the first one's key.
 *
 * The limits, stated plainly. Two accounts with the same fingerprint have nothing but their
 * order to tell them apart. Removing the first of two twins gives the second its key and mark,
 * and so does a re-sync that returns the same two in the other order: the mark and the
 * statement then sit on the other row. The Requests tab says so on any row whose key has a
 * `#n`. Editing an account's institution, name or mask changes its key, which leaves the old
 * mark with no match (shown under its stored label) rather than moving it. Text is cut at
 * {@link KEY_PART_MAX} characters per field before hashing, so two accounts identical in their
 * first 80 characters of a field are twins. The pinned behaviour is in
 * `clientAccountNumberMark.test.ts`.
 */
export function accountKey(account: AccountKeyInput, ordinal = 1): string {
  const rowId = (account.rowId ?? '').trim();
  const base = MINTED_ROW_ID_PATTERN.test(rowId)
    ? `row:${fingerprintDigest(`row|${rowId}`)}`
    : `fp:${fingerprintDigest(`fp|${account.section}|${part(account.institution)}|${part(account.name)}|${part(account.mask)}`)}`;
  return ordinal > 1 ? `${base}#${ordinal}` : base;
}

/** One account as the Requests tab lists it: the input, its key and the label a mark would store. */
export interface RequestAccount extends AccountKeyInput {
  key: string;
  label: string;
  /** Where it came from: the connected (Plaid) snapshot or a hand-typed row. */
  source: 'connected' | 'typed';
  /** Typed investment rows only. A connected account has no owner in what the firm is sent. */
  owner?: string | undefined;
  /** Position in the list the caller passed, to join statements that name it by index. */
  index: number;
}

export interface RequestAccountInput extends AccountKeyInput {
  source: 'connected' | 'typed';
  owner?: string | undefined;
}

/** What to call an account in a label: institution, then name or kind, then the mask. */
export function accountLabel(account: Pick<AccountKeyInput, 'institution' | 'name' | 'mask'>): string {
  const clean = (v: string | undefined) => (v ?? '').replace(/\s+/g, ' ').trim();
  const institution = clean(account.institution);
  const what = clean(account.name);
  const head = [institution, what].filter((s) => s !== '').join(' ') || 'Account';
  const mask = clean(account.mask);
  const label = mask !== '' ? `${head}, ending ${mask}` : head;
  return label.slice(0, ACCOUNT_LABEL_MAX);
}

/**
 * Gives each account its key, in the order given. Pure: the same accounts in any other order
 * produce the same key for every distinguishable account (the twins rule in {@link accountKey}
 * is the only order-dependent part).
 */
export function listRequestAccounts(inputs: readonly RequestAccountInput[]): RequestAccount[] {
  const seen = new Map<string, number>();
  return inputs.map((input, index) => {
    const bare = accountKey(input);
    const ordinal = (seen.get(bare) ?? 0) + 1;
    seen.set(bare, ordinal);
    return {
      section: input.section, rowId: input.rowId, institution: input.institution, name: input.name, mask: input.mask,
      source: input.source, owner: input.owner, index,
      key: accountKey(input, ordinal),
      label: accountLabel(input),
    };
  });
}
